import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useAddMaintenance } from "../hooks/useAssets";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  assetId: string;
  assetName: string;
  currentStatus: string;
};

const TYPES = ["repair", "service", "upgrade", "inspection"] as const;

export function AddMaintenanceModal({
  isOpen,
  onClose,
  assetId,
  assetName,
  currentStatus,
}: Props) {
  const addMutation = useAddMaintenance(assetId);

  const [type, setType] = useState<(typeof TYPES)[number]>("service");
  const [maintenanceDate, setMaintenanceDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [description, setDescription] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [cost, setCost] = useState("");
  const [currency, setCurrency] = useState("AED");
  const [nextDueDate, setNextDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [markBackToAvailable, setMarkBackToAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setType("service");
      setMaintenanceDate(new Date().toISOString().slice(0, 10));
      setDescription("");
      setPerformedBy("");
      setCost("");
      setCurrency("AED");
      setNextDueDate("");
      setNotes("");
      setMarkBackToAvailable(false);
      setError(null);
    }
  }, [isOpen, assetId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!description.trim()) {
      setError("Description is required");
      return;
    }
    setError(null);
    try {
      await addMutation.mutateAsync({
        type,
        maintenanceDate: maintenanceDate
          ? new Date(maintenanceDate).toISOString()
          : new Date().toISOString(),
        description: description.trim(),
        performedBy: performedBy.trim() || null,
        cost: cost ? Number(cost) : null,
        currency,
        nextDueDate: nextDueDate
          ? new Date(nextDueDate).toISOString()
          : null,
        notes: notes.trim() || null,
        updateStatus:
          markBackToAvailable && currentStatus === "under_repair"
            ? "available"
            : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add record");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/30"
        onClick={onClose}
      >
        <div
          className="w-[380px] max-w-[95vw] overflow-hidden rounded-xl border bg-white shadow-xl"
          style={{ borderColor: "hsl(210,30%,85%)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="flex items-center justify-between border-b px-5 py-3"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">
                Maintenance Record
              </h2>
              <div className="truncate text-[12px] text-muted-foreground">
                {assetName}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 p-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>
                  Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={type}
                  onChange={(e) =>
                    setType(e.target.value as (typeof TYPES)[number])
                  }
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label>
                  Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="date"
                  value={maintenanceDate}
                  onChange={(e) => setMaintenanceDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Description <span className="text-destructive">*</span>
              </Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                style={{ borderColor: "hsl(210,30%,85%)" }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Performed By</Label>
              <Input
                value={performedBy}
                onChange={(e) => setPerformedBy(e.target.value)}
                placeholder="e.g. Dell Support"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label>Cost</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {["AED", "USD", "EUR", "GBP", "INR", "SAR"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Next Due Date</Label>
              <Input
                type="date"
                value={nextDueDate}
                onChange={(e) => setNextDueDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              />
            </div>

            {currentStatus === "under_repair" && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={markBackToAvailable}
                  onChange={(e) => setMarkBackToAvailable(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                Mark asset as back to Available
              </label>
            )}

            {error && (
              <div className="rounded-md border bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}

            <div
              className="-mx-5 -mb-5 flex items-center justify-end gap-2 border-t bg-[hsl(210,30%,98%)] px-5 py-3"
              style={{ borderColor: "hsl(210,30%,90%)" }}
            >
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={addMutation.isPending}>
                {addMutation.isPending
                  ? "Saving…"
                  : "Add Maintenance Record"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}
